import React from 'react';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <SEO
        title="Privacy Policy | Stratodata"
        description="Stratodata privacy policy — how we collect, use and protect your data."
        canonical="/privacy"
      />
      <Navigation />

      <main className="flex-1 pt-24 pb-16">
        <div className="container-custom max-w-5xl mx-auto px-4 md:px-6 pt-6 md:pt-8">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Privacy Policy
            </h1>
            <p className="text-sm text-muted-foreground">Stratodata | stratodata.eu</p>
            <p className="text-sm text-muted-foreground mt-1">Last updated: September 29, 2026</p>
          </div>

          <article className="max-w-none text-foreground space-y-8">
            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Stratodata ("we", "us" or "our") is committed to protecting your personal data and respecting your privacy. This Privacy Policy explains how we collect, use, store, and protect personal information when you visit our website at stratodata.eu or engage with our services.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                We process personal data in accordance with Regulation (EU) 2016/679 of the European Parliament and of the Council (General Data Protection Regulation – GDPR) and applicable Czech legislation on personal data protection.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">2. Who We Are (Data Controller)</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                The data controller responsible for your personal data is:
              </p>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Stratodata s.r.o.<br />
                Registered office: Jaurisova 515/4, Michle, 140 00 Prague 4, Czech Republic<br />
                Business ID: 09767754<br />
                VAT ID: CZ09767754<br />
                Contact email: hello@stratodata.eu<br />
                Website: stratodata.eu
              </p>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, you may contact us at the email address above.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">3. What Personal Data We Collect</h2>
              <p className="text-sm font-semibold text-foreground mb-2">3.1 Data You Provide Directly</p>
              <p className="text-muted-foreground leading-relaxed mb-3">
                When you contact us through our website, request a consultation, or engage with our services, we may collect:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 text-sm mb-4">
                <li>Your name and surname</li>
                <li>Email address</li>
                <li>Phone number (if provided)</li>
                <li>Company name and job title</li>
                <li>Content of your messages or enquiries</li>
                <li>Any other information you voluntarily share with us</li>
              </ul>
              <p className="text-sm font-semibold text-foreground mb-2">3.2 Data Collected Automatically</p>
              <p className="text-muted-foreground leading-relaxed mb-3">
                When you visit our website, we may automatically collect certain technical data, including:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 text-sm mb-4">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring URLs</li>
                <li>Date and time of your visit</li>
              </ul>
              <p className="text-sm font-semibold text-foreground mb-2">3.3 Cookies and Similar Technologies</p>
              <p className="text-muted-foreground leading-relaxed">
                Our website may use cookies and similar tracking technologies to enhance your browsing experience and analyse website traffic. Please refer to Section 8 (Cookie Policy) for more details.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">4. Legal Basis for Processing</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We process your personal data on the following legal bases under Article 6 of the GDPR:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 text-sm">
                <li><strong>Legitimate interests (Art. 6(1)(f))</strong>: To respond to your enquiries, improve our services, and maintain our business relationships.</li>
                <li><strong>Contractual necessity (Art. 6(1)(b))</strong>: Where processing is necessary to fulfil a contract with you or to take steps at your request before entering into a contract.</li>
                <li><strong>Consent (Art. 6(1)(a))</strong>: Where you have given your explicit consent, for example when subscribing to marketing communications.</li>
                <li><strong>Legal obligation (Art. 6(1)(c))</strong>: Where we are required to process data to comply with applicable legal obligations.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">5. How We Use Your Personal Data</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We use the personal data we collect for the following purposes:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 text-sm">
                <li>To respond to your enquiries and provide customer support</li>
                <li>To deliver our data analytics, business intelligence, and consulting services</li>
                <li>To manage our contractual relationships and service delivery</li>
                <li>To send you relevant communications about our services (with your consent where required)</li>
                <li>To improve and optimise our website and services</li>
                <li>To comply with legal and regulatory obligations</li>
                <li>To protect our legitimate business interests</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">6. Who We Share Your Data With</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We do not sell, rent, or trade your personal data to third parties. We may share your personal data with:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 text-sm mb-4">
                <li><strong>Service providers:</strong> Trusted third-party providers who assist us in operating our website and delivering our services (e.g., hosting providers, email platforms, analytics tools). These parties act as data processors and are contractually obligated to protect your data.</li>
                <li><strong>Legal authorities:</strong> Where required by law, court order, or regulatory authority.</li>
                <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction, with continued GDPR protections.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                All third-party service providers we engage are required to maintain appropriate technical and organisational security measures and may only process your data on our documented instructions.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">7. How Long We Retain Your Data</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, including to satisfy any legal, accounting, or reporting requirements.
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 text-sm mb-3">
                <li><strong>Contact and enquiry data:</strong> Up to 3 years from the last interaction, unless a longer retention period is required by law or contract.</li>
                <li><strong>Client and contractual data:</strong> For the duration of the business relationship and for up to 10 years thereafter, in accordance with Czech accounting and commercial law.</li>
                <li><strong>Website analytics data:</strong> Typically aggregated and anonymised after 26 months.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                When data is no longer required, we securely delete or anonymise it.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">7a. Automated Decision-Making and Profiling</h2>
              <p className="text-muted-foreground leading-relaxed">
                We do not use your personal data for automated decision-making, including profiling, that produces legal effects concerning you or similarly significantly affects you (Art. 22 GDPR).
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">8. Cookie Policy</h2>
              <p className="text-sm font-semibold text-foreground mb-2">8.1 What Are Cookies</p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Cookies are small text files placed on your device when you visit a website. They help the website function properly, remember your preferences, and provide us with analytics about how our site is used.
              </p>
              <p className="text-sm font-semibold text-foreground mb-2">8.2 Types of Cookies We Use</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 text-sm mb-4">
                <li><strong>Essential cookies:</strong> Necessary for the website to function. These cannot be disabled.</li>
                <li><strong>Analytics cookies:</strong> Help us understand how visitors interact with our website (e.g., Google Analytics).</li>
                <li><strong>Preference cookies:</strong> Remember your settings and choices to improve your experience.</li>
              </ul>
              <p className="text-sm font-semibold text-foreground mb-2">8.3 Your Cookie Choices</p>
              <p className="text-muted-foreground leading-relaxed">
                You can control and delete cookies through your browser settings at any time. Please note that disabling certain cookies may affect the functionality of our website.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">9. International Data Transfers</h2>
              <p className="text-muted-foreground leading-relaxed">
                Stratodata is based in the European Union (Czechia). Some of our service providers may process data outside the EU/EEA, mainly in the USA. In such cases we rely on safeguards under Chapter V of the GDPR: the EU–US Data Privacy Framework (adequacy decision of the European Commission) or Standard Contractual Clauses approved by the European Commission.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">10. Your Rights Under GDPR</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                As a data subject, you have the following rights with respect to your personal data:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 text-sm mb-4">
                <li><strong>Right of access (Art. 15):</strong> You have the right to obtain confirmation of whether we process your personal data and to receive a copy of it.</li>
                <li><strong>Right to rectification (Art. 16):</strong> You have the right to request correction of inaccurate or incomplete personal data.</li>
                <li><strong>Right to erasure / "right to be forgotten" (Art. 17):</strong> You have the right to request deletion of your personal data in certain circumstances.</li>
                <li><strong>Right to restriction of processing (Art. 18):</strong> You have the right to request that we restrict the processing of your data under certain conditions.</li>
                <li><strong>Right to data portability (Art. 20):</strong> You have the right to receive your personal data in a structured, machine-readable format and to transmit it to another controller.</li>
                <li><strong>Right to object (Art. 21):</strong> You have the right to object to processing of your personal data based on legitimate interests or for direct marketing purposes.</li>
                <li><strong>Right to withdraw consent (Art. 7(3)):</strong> Where processing is based on consent, you have the right to withdraw it at any time, without affecting the lawfulness of processing prior to withdrawal.</li>
                <li><strong>Right to lodge a complaint:</strong> You have the right to lodge a complaint with the supervisory authority – the Czech Office for Personal Data Protection (Úřad pro ochranu osobních údajů) at uoou.gov.cz.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                To exercise any of these rights, please contact us at hello@stratodata.eu. We will respond within 30 days of receiving your request.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">11. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, destruction, or alteration. These measures include:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 text-sm mb-4">
                <li>Encryption of data in transit (TLS/SSL)</li>
                <li>Access controls and authentication measures</li>
                <li>Regular security reviews and assessments</li>
                <li>Data minimisation principles</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                While we take data security seriously, please be aware that no method of transmission over the internet or electronic storage is 100% secure. In the event of a personal data breach that poses a high risk to your rights and freedoms, we will notify you without undue delay in accordance with Art. 34 GDPR.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">12. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our website and services are not directed at individuals under the age of 16. We do not knowingly collect personal data from children. If you believe we have inadvertently collected information about a child, please contact us and we will delete it promptly.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">13. Third-Party Links</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">14. Changes to This Privacy Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. We will notify you of material changes by posting the updated policy on our website with the revised date at the top. We encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold mt-8 mb-3">15. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or the processing of your personal data, please contact us:
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Stratodata s.r.o.<br />
                Email: hello@stratodata.eu<br />
                Website: https://stratodata.eu<br />
                Supervisory Authority: Úřad pro ochranu osobních údajů (UOOU) – uoou.gov.cz
              </p>
            </section>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
